import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
  color: string;
}

interface PaymentMethodsProps {
  selectedPackage: string | null;
}

export default function PaymentMethods({ selectedPackage }: PaymentMethodsProps) {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'orange',
      name: 'Orange Money',
      icon: 'ri-smartphone-line',
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'moov',
      name: 'Moov Money',
      icon: 'ri-phone-line',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'mtn',
      name: 'MTN Mobile Money',
      icon: 'ri-mobile-download-line',
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      id: 'wave',
      name: 'Wave',
      icon: 'ri-water-flash-line',
      color: 'from-cyan-500 to-blue-600'
    }
  ];

  const handlePayment = () => {
    if (!selectedPackage || !selectedMethod || !phoneNumber) {
      return;
    }

    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setPhoneNumber('');
    }, 3000);
  };

  if (!selectedPackage) {
    return null;
  }

  return (
    <div className="py-24 px-6 bg-gradient-to-br from-gray-50 to-blue-50" id="payment">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('choosePaymentMethod')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('paymentDescription')}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className={`relative bg-gradient-to-br ${method.color} rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:scale-105 ${
                  selectedMethod === method.id ? 'ring-4 ring-blue-500 scale-105' : ''
                }`}
                onClick={() => setSelectedMethod(method.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
                    <i className={`${method.icon} text-3xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">{method.name}</h3>
                  </div>
                  {selectedMethod === method.id && (
                    <i className="ri-checkbox-circle-fill text-3xl text-white"></i>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selectedMethod && (
            <div className="space-y-6 animate-fadeIn">
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-3">
                  {t('phoneNumber')}
                </label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400"></i>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={t('enterPhoneNumber')}
                    className="w-full pl-14 pr-6 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <button
                onClick={handlePayment}
                disabled={!phoneNumber}
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-5 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-green-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-secure-payment-line text-2xl"></i>
                {t('payNow')}
              </button>

              <div className="flex items-center justify-center gap-3 text-gray-600">
                <i className="ri-shield-check-line text-2xl text-green-600"></i>
                <span className="text-sm">{t('secureTransaction')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
          <div className="bg-white rounded-3xl p-12 max-w-md w-full text-center animate-fadeIn shadow-2xl">
            <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-checkbox-circle-line text-5xl text-white"></i>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('paymentSuccess')}</h3>
            <p className="text-lg text-gray-600 mb-6">{t('paymentSuccessMessage')}</p>
            <div className="bg-blue-50 rounded-2xl p-6">
              <p className="text-sm text-gray-700">{t('activationMessage')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
